import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useLanguage } from '../context/AppContext';
import { Swords, Crown } from 'lucide-react';

/**
 * ClashWidget — dashboard teaser card that shows the current
 * Rouge vs Bleu live score and funnels to /clash on click.
 *
 * Feb 2026 v2: adds a "clash shake" micro-animation that fires
 *   (a) on first mount, and
 *   (b) whenever the leading team flips (tension signal).
 * Motion is ~700ms and respects prefers-reduced-motion.
 */
export default function ClashWidget() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const fr = language === 'fr';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shakeKey, setShakeKey] = useState(0);  // bump to replay animation
  const lastLeaderRef = useRef(null);

  useEffect(() => {
    let active = true;
    const fetch = async () => {
      try {
        const res = await api.get('/clash/status');
        if (active) setData(res.data);
      } catch { /* silent */ }
      finally { if (active) setLoading(false); }
    };
    fetch();
    const iv = setInterval(fetch, 60_000);
    return () => { active = false; clearInterval(iv); };
  }, []);

  // Replay the shake when the leader flips (or on first paint)
  useEffect(() => {
    if (!data?.rouge || !data?.bleu) return;
    const leader = data.rouge.points > data.bleu.points ? 'rouge'
                  : data.bleu.points > data.rouge.points ? 'bleu'
                  : 'tie';
    if (lastLeaderRef.current !== leader) {
      lastLeaderRef.current = leader;
      setShakeKey(k => k + 1);
    }
  }, [data]);

  if (loading || !data) return null;
  if (!data.rouge || !data.bleu) return null;

  const rougePts = data.rouge.points || 0;
  const bleuPts = data.bleu.points || 0;
  const total = Math.max(rougePts + bleuPts, 1);
  const rougePct = Math.round((rougePts / total) * 100);
  const bleuPct = 100 - rougePct;
  const leading = rougePts > bleuPts ? 'rouge' : bleuPts > rougePts ? 'bleu' : null;
  const gap = Math.abs(rougePts - bleuPts);

  return (
    <button
      type="button"
      onClick={() => navigate('/clash')}
      className="w-full mb-4 p-4 rounded-xl bg-gradient-to-r from-red-500/10 via-secondary/40 to-blue-500/10 border border-border hover:border-primary/40 active:scale-[0.99] transition-all text-left group overflow-hidden"
      data-testid="dashboard-clash-widget"
      aria-label={fr ? 'Ouvrir Clash des Meutes' : 'Open Clash of Packs'}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Swords className="w-4 h-4 text-primary flex-shrink-0" />
          <span className="text-xs font-heading uppercase tracking-wider text-foreground truncate">
            {fr ? 'Clash des Meutes' : 'Clash of Packs'}
          </span>
          {leading && gap > 0 && (
            <span className={`flex items-center gap-0.5 text-[10px] font-bold uppercase ${
              leading === 'rouge' ? 'text-red-400' : 'text-blue-400'
            }`}>
              <Crown className="w-3 h-3" />
              {leading === 'rouge' ? '🔴' : '🔵'} +{gap}
            </span>
          )}
        </div>
        <span className="text-[10px] text-muted-foreground group-hover:text-primary transition-colors">
          {fr ? 'Voir →' : 'Open →'}
        </span>
      </div>

      {/* Score row */}
      <div className="flex items-baseline justify-between mb-1.5 font-mono">
        <span
          key={`rl-${shakeKey}`}
          className="text-lg font-bold text-red-400 animate-clash-shake-left inline-block"
          data-testid="clash-widget-rouge"
        >
          🔴 {rougePts.toLocaleString()}
        </span>
        <span
          key={`d-${shakeKey}`}
          className="text-[10px] text-primary uppercase tracking-wider animate-clash-divider inline-block px-1 rounded-full"
        >
          vs
        </span>
        <span
          key={`br-${shakeKey}`}
          className="text-lg font-bold text-blue-400 animate-clash-shake-right inline-block"
          data-testid="clash-widget-bleu"
        >
          {bleuPts.toLocaleString()} 🔵
        </span>
      </div>

      {/* Dual-fill bar — wrapper shakes once on leader change */}
      <div
        key={`bar-${shakeKey}`}
        className="relative h-2 rounded-full bg-border overflow-hidden flex animate-clash-shake-left"
      >
        <div
          className="bg-gradient-to-r from-red-500 to-red-400 transition-all duration-700 ease-out"
          style={{ width: `${rougePct}%` }}
          data-testid="clash-widget-bar-rouge"
        />
        <div
          className="bg-gradient-to-r from-blue-400 to-blue-500 transition-all duration-700 ease-out"
          style={{ width: `${bleuPct}%` }}
          data-testid="clash-widget-bar-bleu"
        />
      </div>
    </button>
  );
}
